<template>
  <v-app-bar v-if="display.mobile.value" elevation="1" absolute>
    <template #prepend>
      <v-app-bar-nav-icon @click="drawer = !drawer" />
    </template>

    <v-toolbar-title density="compact">
      <router-link
        to="/"
        class="text-decoration-none text-primary text-body-medium font-weight-black"
      >
        {{ APP_NAME }}
      </router-link>
    </v-toolbar-title>
  </v-app-bar>

  <v-navigation-drawer
    v-bind="$attrs"
    elevation="2"
    class="pa-4"
    v-model="drawer"
    :permanent="!display.mobile.value"
    :temporary="display.mobile.value"
    :location="display.mobile.value ? 'left' : undefined"
    :width="280"
  >
    <!-- Brand -->
    <router-link
      v-if="!display.mobile.value"
      to="/"
      class="text-decoration-none d-flex align-center mb-8"
      :class="logo.className"
    >
      <v-hover v-slot="{ isHovering, props }">
        <v-avatar v-bind="props" size="48" :rounded="UI.BORDER_RADIUS">
          <v-img alt="Logo" cover :src="isHovering ? '/logo/spezi.jpeg' : logo?.src" />
        </v-avatar>
      </v-hover>

      <div class="ms-3">
        <div class="text-h6 font-weight-bold navigation-menu-revamp__brand">{{ APP_NAME }}</div>
      </div>
    </router-link>

    <!-- Main Navigation: General -->
    <v-card variant="tonal" class="mb-4" :rounded="UI.BORDER_RADIUS">
      <NavigationMenuItems title="General" :links="SIDEBAR_GENERAL_LINKS" />
    </v-card>

    <!-- Main Navigation: Configuration -->
    <v-card variant="tonal" class="mb-4" :rounded="UI.BORDER_RADIUS">
      <NavigationMenuItems title="Configuration" :links="SIDEBAR_CONFIGURATION_LINKS" />
    </v-card>

    <!-- Diagnostics -->
    <v-card variant="tonal" class="mb-4" :rounded="UI.BORDER_RADIUS">
      <NavigationMenuItems title="Diagnostics" :links="SIDEBAR_DIAGNOSTICS_LINKS" />
    </v-card>

    <template #append>
      <div class="mt-4">
        <v-divider class="mb-3" />

        <v-menu location="top" offset="8" width="260">
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              class="px-4 py-3 cursor-pointer"
              :rounded="UI.BORDER_RADIUS"
            >
              <template #default>
                <div class="d-flex flex-column">
                  <div class="text-subtitle-1 font-weight-bold text-truncate">
                    {{ user?.name }}
                  </div>

                  <div class="mt-1 d-flex ga-1">
                    <v-chip variant="tonal" size="small" label :color="badgeInfo?.color">
                      <v-icon size="16" start> mdi-code-tags </v-icon>

                      {{ badgeInfo?.text }}
                    </v-chip>

                    <v-chip
                      variant="tonal"
                      size="small"
                      color="grey"
                      label
                      :title="`Build commit ${commitHash}`"
                    >
                      <v-icon size="16"> mdi-source-commit </v-icon>

                      <span v-if="commitHash.split('-')[1]" class="d-inline-block ml-1">
                        {{ commitHash.split('-')[1] }}
                      </span>
                    </v-chip>
                  </div>
                </div>
              </template>

              <template #append>
                <v-btn
                  variant="outlined"
                  density="comfortable"
                  color="grey"
                  rounded="circle"
                  size="20"
                  icon
                >
                  <v-icon size="15"> mdi-chevron-up </v-icon>
                </v-btn>
              </template>
            </v-list-item>
          </template>

          <TableCard>
            <v-list density="comfortable" nav>
              <!-- User info -->
              <v-list-item>
                <v-list-item-title> {{ user?.name }} </v-list-item-title>

                <v-list-item-subtitle> {{ user?.idTokenClaims?.emails?.[0] }}</v-list-item-subtitle>
              </v-list-item>

              <v-divider class="my-2" />

              <!-- User navigation -->
              <NavigationMenuItems :links="USER_MENU_LINKS" />

              <v-divider class="my-2" />

              <!-- Appearance -->
              <NavigationMenuThemeCta />

              <v-divider class="my-2" />

              <v-list-item
                prepend-icon="bi-box-arrow-right"
                title="Logout"
                :to="{ name: RouteNames.Logout }"
                :rounded="UI.BORDER_RADIUS"
              />
            </v-list>
          </TableCard>
        </v-menu>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useDisplay, useTheme } from 'vuetify';

import TableCard from '@/components/common/table-card/TableCard.vue';
import NavigationMenuItems from '@/components/navigation-menu/NavigationMenuItems.vue';
import NavigationMenuThemeCta from '@/components/navigation-menu/NavigationMenuThemeCta.vue';
import {
  APP_NAME,
  SIDEBAR_CONFIGURATION_LINKS,
  SIDEBAR_DIAGNOSTICS_LINKS,
  SIDEBAR_GENERAL_LINKS,
  USER_MENU_LINKS,
} from '@/components/navigation/constants/NAVIGATION';
import { useAuth } from '@/composable/msal';
import { UI } from '@/layouts/constants/Layout';
import { RouteNames } from '@/router/constants/routeName';
import { checkIfProduction } from '@/utils';

defineOptions({ inheritAttrs: false });

const display = useDisplay();
const auth = useAuth();
const theme = useTheme();

const drawer = ref<boolean>(!display.mobile.value);
const commitHash = computed(() => {
  const commit = import.meta.env.VITE_COMMIT;
  return commit === '${npm_package_version}' ? 'local' : commit;
});
const user = auth.getAccountInfo();
const logo = computed(() => {
  return theme?.name.value === 'light'
    ? {
        src: '/logo/logo.svg',
        className: 'text-black',
      }
    : {
        src: '/logo/logo-dark.svg',
        className: 'text-white',
      };
});

const badgeInfo = computed(() => {
  return checkIfProduction()
    ? {
        text: 'Production',
        color: 'success',
      }
    : {
        text: 'Development',
        color: 'error',
      };
});
</script>

<style lang="scss" scoped>
.navigation-menu-revamp__brand {
  max-width: 9.375rem;
  line-height: 1.2;
}
</style>
